import { useState, useEffect } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { GridColDef, GridColumnVisibilityModel } from "@mui/x-data-grid";
import { getJwtTokenFromIdToken } from "../auth/AuthFanc";
import UpdateButton from "../../atoms/Button/UpdateButton";
import {
  CustomContainer,
  StyledDataGrid,
  customDataGridLocaleTextAll,
  customDataGridLocaleTextLoading,
  customDataGridLocaleTextNoData,
} from "../../../styles/styles";
import IconButtonWithTooltip from "../../atoms/Button/IconButtonWithTooltip";
import EditIcon from "@mui/icons-material/Edit";
import { CircleOutlined, Close } from "@mui/icons-material";
import ftsmartkeyIcon from "../../../assets/images/ftsmartkey_icon_small.png";
import mirairodoorIcon from "../../../assets/images/mirairodoor_icon_small.png";

const enum EnabledService {
  None = 0,
  AalOnly,
  SciOnly,
  Both,
}

interface Door {
  autoSdcId: number;
  sdcSerialNumber: string;
  sdcUuid: string;
  doorPlace: string;
  doorId: string;
  memo: string;
  shippingDate: Date;
  isRegisteredAsDoor: boolean;
  isOnline: boolean;
  enabledService: EnabledService;
  isBroken: boolean;
  lastDisconnectedAt: Date;
  lastDownloadedAt: Date;
  building: {
    autoBuildingId: number;
    buildingId: string;
    buildingManagementId: string;
    buildingName: string;
    buildingAddress: string;
    fulltechOffice?: {
      officeName: string;
    };
  };
}

const DoorListPage = () => {
  const navigate = useNavigate();

  const [data, setData] = useState<Door[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>(undefined);

  // このstateはチェックボックスの状態を制御します

  const [filterRegistered, setFilterRegistered] = useState(true);
  const [filterUnregistered, setFilterUnregistered] = useState(true);
  const [filterNormalDevices, setFilterNormalDevices] = useState(true);
  const [filterBrokenDevices, setFilterBrokenDevices] = useState(false);

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({
      buildingId: false, // 物件システムIDを非表示
      buildingAddress: false, // 物件住所を非表示
    });

  const fetchData = async () => {
    const jwtToken = await getJwtTokenFromIdToken();

    const queryParams = new URLSearchParams();

    // チェックボックスがすべて無効の場合はデータを取得しない
    if (
      !filterRegistered &&
      !filterUnregistered &&
      !filterNormalDevices &&
      !filterBrokenDevices
    ) {
      setData([]); // データを空にする
      setIsLoading(false);
      return;
    }

    // 物件登録のフィルタリング
    if (!(filterRegistered && filterUnregistered)) {
      if (filterRegistered) {
        queryParams.append("isRegisteredAsDoor", "true");
      } else if (filterUnregistered) {
        queryParams.append("isRegisteredAsDoor", "false");
      }
    }

    // デバイス状態のフィルタリング
    if (!(filterNormalDevices && filterBrokenDevices)) {
      if (filterNormalDevices) {
        queryParams.append("isBroken", "false");
      }
      if (filterBrokenDevices) {
        queryParams.append("isBroken", "true");
      }
    }

    const queryString = queryParams.toString();

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_ENDPOINT}/sdcs?${queryString}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: jwtToken,
          },
        }
      );
      const jsonData = await response.json();

      const sdcs = (jsonData.sdcs || []).map((sdc: any) => {
        let enabledService = EnabledService.None;
        if (sdc.isAalEnabled && sdc.isSciEnabled) {
          enabledService = EnabledService.Both;
        } else if (sdc.isAalEnabled) {
          enabledService = EnabledService.AalOnly;
        } else if (sdc.isSciEnabled) {
          enabledService = EnabledService.SciOnly;
        }
        return { ...sdc, enabledService };
      });

      // SDC出荷日のフィルタリング
      const today = new Date();
      today.setHours(0, 0, 0, 0); // 今日の0時0分0秒に設定

      const filteredSdcs = sdcs.filter((sdc: Door) => {
        // 出荷日が未設定の場合は除外
        if (!sdc.shippingDate || sdc.shippingDate.toString() === "0000-00-00 00:00:00") {
          return false;
        }

        // 出荷日が明日以降の場合は表示しない
        const shippingDate = new Date(sdc.shippingDate);
        shippingDate.setHours(0, 0, 0, 0); // 時刻部分を0に設定して日付のみで比較
        
        // 明日の日付を取得
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);
        
        return shippingDate < tomorrow; // 明日より前（今日まで）の出荷日のみ表示
      });

      setData(filteredSdcs);
      setIsLoading(false);
    } catch (error: any) {
      setError(error);
      setIsLoading(false);
      setData([]);
    }
  };

  useEffect(() => {
    // 全チェックボックスが無効の場合はデータをリセット
    if (
      !filterRegistered &&
      !filterUnregistered &&
      !filterNormalDevices &&
      !filterBrokenDevices
    ) {
      setData([]); // データをクリア
      setIsLoading(false); // ローディング終了
      return;
    }

    // データを取得
    fetchData();
  }, [
    filterRegistered,
    filterUnregistered,
    filterNormalDevices,
    filterBrokenDevices,
  ]);

  const handleEditDoorClick = (autoSdcId: number) => {
    navigate(`/doors/` + autoSdcId);
    console.log(
      `SDC（ドア）編集 button clicked 選択されたSDC（ドア）: ${autoSdcId}`
    );
  };

  if (isLoading) {
    <Box display="flex" justifyContent="center" alignItems="center">
      <CircularProgress /> {/* MUIの CircularProgress コンポーネント */}
    </Box>;
  }

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  const columns: GridColDef[] = [
    { field: "sdcSerialNumber", headerName: "SDCSN", flex: 1 },
    {
      field: "shippingDate",
      headerName: "SDC出荷日",
      flex: 1,
      renderCell: (params) => {
        const dateValue = params.value;

        // Check for null, undefined, or invalid date strings like "0000-00-00 00:00:00"
        if (
          !dateValue ||
          dateValue === "0000-00-00 00:00:00" ||
          isNaN(new Date(dateValue).getTime())
        ) {
          return (
            <Tooltip title="未設定">
              <span>未設定</span>
            </Tooltip>
          );
        }

        // Format valid dates
        const dateStr = new Date(dateValue).toLocaleDateString("ja-JP", {
          timeZone: "Asia/Tokyo",
        });
        return (
          <Tooltip title={dateStr}>
            <span>{dateStr}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "isOnline",
      headerName: "接続状態",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value ? "オンライン" : "オフライン"}>
          {params.value ? (
            <CircleOutlined sx={{ color: "green" }} />
          ) : (
            <Close sx={{ color: "red" }} />
          )}
        </Tooltip>
      ),
    },
    {
      field: "isBroken",
      headerName: "デバイス状態",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value ? "故障中" : "正常"}>
          <span>{params.value ? "故障中" : "正常"}</span>
        </Tooltip>
      ),
    },
    {
      field: "lastDisconnectedAt",
      headerName: "最終切断日時",
      flex: 1,
      renderCell: (params) => {
        if (!params.value) {
          // nullまたはundefinedの場合の表示
          return (
            <Tooltip title="記録なし">
              <span>記録なし</span>
            </Tooltip>
          );
        }
    
        const dateTimeStr = new Date(params.value).toLocaleString("ja-JP", {
          timeZone: "Asia/Tokyo", // 日本時間
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        });
    
        return (
          <Tooltip title={dateTimeStr}>
            <span>{dateTimeStr}</span>
          </Tooltip>
        );
      },
    },    
    {
      field: "enabledService",
      headerName: "有効サービス",
      flex: 1,
      renderCell: (params) => {
        const enabledService: EnabledService = params.value;
        return (
          <div>
            {(enabledService === EnabledService.AalOnly ||
              enabledService === EnabledService.Both) && (
              <Tooltip title="FTスマートキー">
                <img
                  src={ftsmartkeyIcon}
                  alt="FTスマートキー icon"
                  style={{
                    height: "30px",
                    marginRight: "3px",
                  }}
                />
              </Tooltip>
            )}
            {(enabledService === EnabledService.SciOnly ||
              enabledService === EnabledService.Both) && (
              <Tooltip title="ミライロドア">
                <img
                  src={mirairodoorIcon}
                  alt="ミライロドア icon"
                  style={{
                    height: "30px",
                  }}
                />
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      field: "lastDownloadedAt",
      headerName: "SDCが最後に鍵をDLした日時",
      flex: 1,
      renderCell: (params) => {
        if (!params.value) {
          // nullまたはundefinedの場合の表示
          return (
            <Tooltip title="記録なし">
              <span>記録なし</span>
            </Tooltip>
          );
        }

        const dateStr = new Date(params.value).toLocaleString("ja-JP", {
          timeZone: "Asia/Tokyo",
        });
        return (
          <Tooltip title={dateStr}>
            <span>{dateStr}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "buildingId",
      headerName: "物件システムID",
      flex: 1,
      valueGetter: (params: any) => params.row.building?.buildingId,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "buildingManagementId",
      headerName: "取付先コード",
      flex: 1,
      valueGetter: (params: any) => params.row.building?.buildingManagementId,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "officeName",
      headerName: "管轄拠点",
      flex: 1,
      valueGetter: (params: any) =>
        params.row.building?.fulltechOffice?.officeName,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value || "未設定"}</span>
        </Tooltip>
      ),
    },
    {
      field: "buildingName",
      headerName: "取付先名（物件名称）",
      flex: 2,
      valueGetter: (params: any) => params.row.building?.buildingName,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "buildingAddress",
      headerName: "物件住所",
      flex: 2,
      valueGetter: (params: any) => params.row.building?.buildingAddress,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "doorPlace",
      headerName: "設置場所",
      flex: 2,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "memo",
      headerName: "メモ",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "autoSdcId",
      headerName: "操作",
      flex: 1,
      renderCell: (params: any) => (
        <IconButtonWithTooltip
          title="編集"
          onClick={() => handleEditDoorClick(params.row.autoSdcId)}
          icon={<EditIcon />}
        />
      ),
    },
  ];

  return (
    <CustomContainer maxWidth={false}>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        alignItems="flex-start"
        marginBottom={2}
      >
        <Typography variant="h5" component="h2" gutterBottom>
          SDC（ドア）一覧
        </Typography>
        <Box display="flex" width="100%" justifyContent="flex-end">
          <FormControlLabel
            control={
              <Checkbox
                checked={filterRegistered}
                onChange={(event) => {
                  setFilterRegistered(event.target.checked);
                }}
              />
            }
            label="物件登録済みを表示"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={filterUnregistered}
                onChange={(event) => {
                  setFilterUnregistered(event.target.checked);
                }}
              />
            }
            label="物件未登録を表示"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={filterNormalDevices}
                onChange={(event) => {
                  setFilterNormalDevices(event.target.checked);
                }}
              />
            }
            label="正常デバイスを表示"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={filterBrokenDevices}
                onChange={(event) => {
                  setFilterBrokenDevices(event.target.checked);
                }}
              />
            }
            label="故障中デバイスを表示"
          />

          {/* ドア（sdc）に物件紐付けは物件詳細でやるので、ここからの動線は一旦削除 */}
          {/* <Button variant="outlined" color="primary" onClick={handleNewDoorClick} sx={{ marginRight: 1 }}>
            ドア新規登録
          </Button> */}
          <UpdateButton />
        </Box>
      </Box>
      <div style={{ height: 700, width: "100%" }}>
        <StyledDataGrid
          rows={data}
          columns={columns}
          getRowId={(row) => row.autoSdcId}
          disableRowSelectionOnClick
          localeText={
            isLoading
              ? customDataGridLocaleTextLoading
              : data.length === 0
              ? customDataGridLocaleTextNoData
              : customDataGridLocaleTextAll
          }
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={(newModel) =>
            setColumnVisibilityModel(newModel)
          }
        />
      </div>
    </CustomContainer>
  );
};

export default DoorListPage;
